import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {VariableSizeList as VirtualList} from 'react-window';
import InfiniteLoader from "react-window-infinite-loader";
import {Spin} from 'antd';
import {useLocation, useNavigate} from 'react-router-dom';
import {useQuery} from "@tanstack/react-query";
import {useJobs} from './hooks/useJobs';
import {JobItem} from "./components/JobItem.tsx";
import {AbsLoader} from "../candidatePool/CandidatePool.tsx";
import {TableRow} from "../candidatePool/TableRow.tsx";
import {TableWrapper} from "candidate/src/pages/jobPool/JobPool.tsx";
import {PageTitle} from "candidate/src/components/PageTitle.tsx";
import {getJobSubscriptions} from "../../services/api.ts";
import {IJobSubscription} from "../../services/types.ts";

const CONTAINER_HEIGHT = window.innerHeight - 133;
const LIMIT = 20;

export const Jobs: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage} = useJobs(LIMIT);

    const {data: jobSubscriptions} = useQuery({
        queryKey: ['jobSubscriptions'],
        queryFn: getJobSubscriptions,
    });

    const subscriptionMap = useMemo(() => {
        const map = new Map<number, IJobSubscription>();
        jobSubscriptions?.forEach(sub => {
            if (sub.status === 'active' || sub.status === 'expiring_soon' ||
                (sub.status === 'canceled' && sub.expiration_date && sub.expiration_date > Date.now())) {
                const jobDemandId = Number(sub.job_demand_id);
                if (Number.isFinite(jobDemandId)) {
                    const existing = map.get(jobDemandId);
                    if (!existing || existing.status === 'canceled') {
                        map.set(jobDemandId, sub);
                    }
                }
            }
        });
        return map;
    }, [jobSubscriptions]);

    const listRef = useRef<VirtualList | null>(null);
    const sizeMap = useRef<{ [key: number]: number }>({});
    const [scrollPosition, setScrollPosition] = useState(0);
    const initialLoadRef = useRef(true);

    const flattenedData = useMemo(() => {
        if (!data || (data.pages.length === 1 && data.pages[0][0]?.id === 0)) {
            return [];
        }
        return data.pages.flatMap(page => Array.isArray(page) ? page : []);
    }, [data]);

    const itemCount = hasNextPage ? flattenedData.length + 1 : flattenedData.length;

    const loadMoreItems = useCallback(() => {
        if (!isFetchingNextPage) {
            return fetchNextPage().then(() => {
            });
        }
        return Promise.resolve();
    }, [isFetchingNextPage, fetchNextPage]);

    const isItemLoaded = useCallback((index: number) => {
        return !hasNextPage || index < flattenedData.length;
    }, [hasNextPage, flattenedData]);

    const setSize = useCallback((index: number, size: number) => {
        sizeMap.current = {...sizeMap.current, [index]: size};
        if (listRef.current) {
            listRef.current!.resetAfterIndex(index);
        }
    }, []);

    const getSize = (index: number) => sizeMap.current[index] || 129;

    const handleScroll = useCallback(({scrollOffset}: { scrollOffset: number }) => {
        if (!initialLoadRef.current) {
            setScrollPosition(scrollOffset);
            sessionStorage.setItem('jobsScrollPosition', scrollOffset.toString());
        }
    }, []);

    useEffect(() => {
        const savedScrollPosition = sessionStorage.getItem('jobsScrollPosition');
        if (savedScrollPosition) {
            const parsedPosition = parseInt(savedScrollPosition, 10);
            setScrollPosition(parsedPosition);

            setTimeout(() => {
                listRef.current?.scrollTo(parsedPosition);
                initialLoadRef.current = false;
            }, 0);
        } else {
            initialLoadRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (location.state?.fromJobDetails) {
            const savedScrollPosition = sessionStorage.getItem('jobsScrollPosition');
            if (savedScrollPosition) {
                const parsedPosition = parseInt(savedScrollPosition, 10);
                setScrollPosition(parsedPosition);
                listRef.current?.scrollTo(parsedPosition);
            }
        } else {
            setScrollPosition(0);
            sessionStorage.removeItem('jobsScrollPosition');
        }
    }, [location]);

    return (
        <div>
            <PageTitle
                mainBtnType="default"
                title="Meine Stellen"
            />
            {isFetching && <AbsLoader><Spin size="large"/></AbsLoader>}
            <TableWrapper>
                <InfiniteLoader
                    isItemLoaded={isItemLoaded}
                    itemCount={itemCount}
                    loadMoreItems={loadMoreItems}
                >
                    {({onItemsRendered, ref}) => (
                        <VirtualList
                            height={CONTAINER_HEIGHT}
                            itemCount={itemCount}
                            itemSize={getSize}
                            onItemsRendered={onItemsRendered}
                            ref={(listInstance) => {
                                ref(listInstance);
                                listRef.current = listInstance;
                            }}
                            width="100%"
                            initialScrollOffset={scrollPosition}
                            onScroll={handleScroll}
                        >
                            {({index, style}) => (
                                <div style={style}>
                                    <TableRow
                                        index={index}
                                        setSize={setSize}
                                        flattenedData={flattenedData}
                                    >
                                        {() => <JobItem
                                            key={flattenedData[index]?.id || index}
                                            item={flattenedData[index]}
                                            subscription={flattenedData[index]?.id != null
                                                ? subscriptionMap.get(Number(flattenedData[index].id))
                                                : undefined}
                                            onEdit={() => navigate(`/jobs/${flattenedData[index].id}`)}
                                        />}
                                    </TableRow>
                                </div>
                            )}
                        </VirtualList>
                    )}
                </InfiniteLoader>
            </TableWrapper>
        </div>
    );
};
